const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const DAY_NAME_PATTERN = '(?:sun(?:day)?|mon(?:day)?|tue(?:s(?:day)?)?|wed(?:nesday)?|thu(?:r?s(?:day)?)?|fri(?:day)?|sat(?:urday)?)';
const MONTH_NAME_PATTERN = '(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)';
const LEADING_DATE_PATTERN = new RegExp(
  `^(?:${DAY_NAME_PATTERN}\\.?\\s+)?(?<date>\\d{1,2}\\/\\d{1,2}(?:\\/\\d{2,4})?|${MONTH_NAME_PATTERN}\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,?\\s+\\d{2,4})?)(?<separator>[\\s,;:\\-–—.]*)`,
  'i'
);
const TITLE_DESCRIPTOR_PATTERN = /\b(?:note|notes|update|report|summary|session|log|letter|message|email|check-in|check in)\b/i;
const GENERIC_TITLE_PATTERN = /^(?:original note|source story|saved source|caregiver note|daily log|note|update|message)$/i;
const FILE_NAME_PATTERN = /\.(?:pdf|png|jpe?g|heic|webp|gif|docx?|txt)$/i;
const SIGNATURE_NAME_PATTERN = "[A-Z][A-Za-z'’.-]*(?:[ \\t]+[A-Z][A-Za-z'’.-]*){0,2}";
const SAME_LINE_SIGNOFF_PATTERN = new RegExp(
  `^(?:love|thank you|thanks|best|best regards|regards|sincerely|take care),[ \\t]*(?<name>${SIGNATURE_NAME_PATTERN})[.!]?$`,
  'i'
);
const SIGNOFF_ONLY_PATTERN = /^(?:love|thank you|thanks|best|best regards|regards|sincerely|take care),?$/i;
const NAME_ONLY_PATTERN = new RegExp(`^(?<name>${SIGNATURE_NAME_PATTERN})[.!]?$`);
const DASH_SIGNATURE_PATTERN = new RegExp(`^[-–—][ \\t]*(?<name>${SIGNATURE_NAME_PATTERN})[.!]?$`);
const INVALID_SIGNATURE_NAME_PATTERN = /^(?:all|everyone|everybody|you|your family|the team|for reading|for your help|for your support)$/i;

const cleanTitle = (value = '') => normalizeText(value)
  .replace(/[♡♥❤︎️]+/gu, '')
  .replace(/[\s\-–—,:;]+$/u, '')
  .trim();

export const getSourceTextForTitle = (source = {}) => String(
  source.metadata?.originalRawText
  || source.metadata?.rawSourceText
  || source.rawSourceText
  || source.rawText
  || source.originalText
  || source.source?.body
  || source.sourceMeta?.body
  || source.sourceText
  || source.cleanedText
  || ''
).trim();

export const getSourceFileName = (source = {}) => cleanTitle(
  source.sourceMetadata?.fileName
  || source.metadata?.source?.fileName
  || source.fileName
  || source.source?.fileName
  || source.imageAttachments?.[0]?.fileName
  || source.metadata?.source?.imageAttachments?.[0]?.fileName
  || ''
);

const getRecognizedDocumentTitle = (text = '') => {
  if (/\bbehavior support plan\b/i.test(text)) return 'Behavior Support Plan';
  if (/\biep\b/i.test(text)) return 'IEP';
  if (/\bannual goal\b/i.test(text)) return 'Annual Goal';
  if (/\bpreferred foods\b|\bfoods working on\b|\bfeeding plan\b/i.test(text)) return 'Feeding Plan';
  if (/\bmedication instructions?\b/i.test(text)) return 'Medication Instructions';
  if (/\btherapy recommendation\b/i.test(text)) return 'Therapy Recommendation';
  return '';
};

const validateSignatureName = (value = '') => {
  const name = cleanTitle(value).replace(/[.!]+$/u, '').trim();
  return name && NAME_ONLY_PATTERN.test(name) && !INVALID_SIGNATURE_NAME_PATTERN.test(name) ? name : '';
};

const getClosingSignatureName = (sourceText = '') => {
  const closingLines = String(sourceText || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean).slice(-4);
  for (let index = closingLines.length - 1; index >= 0; index -= 1) {
    const sameLineName = validateSignatureName(closingLines[index].match(SAME_LINE_SIGNOFF_PATTERN)?.groups?.name);
    if (sameLineName) return sameLineName;
    const dashName = validateSignatureName(closingLines[index].match(DASH_SIGNATURE_PATTERN)?.groups?.name);
    if (dashName) return dashName;
    if (index > 0 && SIGNOFF_ONLY_PATTERN.test(closingLines[index - 1])) {
      const multilineName = validateSignatureName(closingLines[index].match(NAME_ONLY_PATTERN)?.groups?.name);
      if (multilineName) return multilineName;
    }
  }
  return '';
};

const isUsefulExplicitTitle = (value = '') => {
  const title = cleanTitle(value);
  return Boolean(title && !GENERIC_TITLE_PATTERN.test(title) && !FILE_NAME_PATTERN.test(title));
};

const parseLeadingDate = (firstLine = '') => {
  const normalized = normalizeText(firstLine);
  const match = normalized.match(LEADING_DATE_PATTERN);
  if (!match?.groups?.date) return { declaredDateLabel: '', remainingText: normalized };
  return {
    declaredDateLabel: normalizeText(match.groups.date),
    remainingText: normalized.slice(match[0].length).trim(),
  };
};

const formatDeclaredDate = (value = '') => {
  const normalized = normalizeText(value);
  const numericMatch = normalized.match(/^(?<month>\d{1,2})\/(?<day>\d{1,2})(?:\/(?<year>\d{2,4}))?$/);
  if (numericMatch?.groups) {
    const month = Number(numericMatch.groups.month);
    const day = Number(numericMatch.groups.day);
    let year = Number(numericMatch.groups.year || 0);
    if (year > 0 && year < 100) year += 2000;
    const date = new Date(year || 2000, month - 1, day);
    if (date.getMonth() === month - 1 && date.getDate() === day) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(year ? { year: 'numeric' } : {}) });
    }
  }
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? normalized : parsed.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(/\d{2,4}/.test(normalized) ? { year: 'numeric' } : {}),
  });
};

export const resolveTimelineContentTitle = (source = {}, { includeSender = true } = {}) => {
  const sourceText = getSourceTextForTitle(source);
  if (includeSender) {
    const senderName = cleanTitle(source.sourceMeta?.senderName || source.source?.senderName || source.metadata?.source?.senderName || '');
    if (senderName) return senderName;
    const signatureName = getClosingSignatureName(sourceText);
    if (signatureName) return signatureName;
  }

  const explicitTitle = cleanTitle(source.sourceTitle || source.metadata?.sourceTitle || source.metadata?.source?.title || '');
  if (isUsefulExplicitTitle(explicitTitle)) return explicitTitle;
  const subject = cleanTitle(source.sourceMeta?.subject || source.source?.subject || source.metadata?.source?.subject || source.subject || '');
  if (isUsefulExplicitTitle(subject)) return subject;

  const firstLine = sourceText.split(/\r?\n/).map((line) => line.trim()).find(Boolean) || '';
  const { remainingText } = parseLeadingDate(firstLine);
  const heading = cleanTitle(remainingText);
  return heading && heading.length <= 80 && TITLE_DESCRIPTOR_PATTERN.test(heading) && !/[.!?]\s+\S/.test(heading)
    ? heading
    : '';
};

const getDeclaredDateTitle = (source = {}) => {
  const firstLine = getSourceTextForTitle(source).split(/\r?\n/).map((line) => line.trim()).find(Boolean) || '';
  const { declaredDateLabel } = parseLeadingDate(firstLine);
  return declaredDateLabel ? formatDeclaredDate(declaredDateLabel) : '';
};

const getLoggedDateTitle = (source = {}) => {
  const candidates = [source.capturedAt, source.createdAt, source.metadata?.createdAt, source.source?.createdAt, source.loggedAt];
  for (const candidate of candidates) {
    const date = candidate?.toDate?.() || (candidate ? new Date(candidate) : null);
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      return `Logged ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
  }
  return '';
};

export const resolveTitle = (source = {}, context = 'timeline-scene', options = {}) => {
  const sourceText = getSourceTextForTitle(source);
  if (context === 'library') {
    return cleanTitle(source.metadata?.source?.displayTitle || source.sourceMetadata?.displayTitle)
      || cleanTitle(source.title)
      || getRecognizedDocumentTitle(sourceText)
      || getSourceFileName(source).replace(/\.[^.]+$/, '').replace(/^[0-9]+[_\s-]+/, '').replace(/[_-]+/g, ' ')
      || sourceText.split(/\n+/).map((line) => line.trim()).find(Boolean)?.slice(0, 72)
      || 'Care document';
  }

  const contentTitle = resolveTimelineContentTitle(source, { includeSender: context === 'timeline-scene' });
  const sourceTitle = contentTitle || getDeclaredDateTitle(source);
  if (sourceTitle) return sourceTitle;

  if (context === 'timeline-scene') return 'Care note';

  return cleanTitle(options.loggedDateLabel)
    || getLoggedDateTitle(source)
    || 'Care note';
};

export default resolveTitle;
